import * as React from "react";
import { clsx } from "clsx";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { SnapshotGridTableRowResult } from "./rowResult";
import { SnapshotGridTableRowNotes } from "./rowNotes";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import {
  SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH,
  getSnapshotGridColumnStyle,
  getSnapshotGridColumnWidth,
  type SnapshotGridColumnWidths,
} from "./layout";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import type { SnapshotGridRowModel } from "@/modules/snapshots/grid/rowModel";
import { SnapshotGridTablePipelineRowResult } from "./rowResult";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  promptHeaders: string[];
  noteHeaders: string[];
  dataColumns?: SnapshotGridField[];
  businessReportOutcomeResultWithEvent: ReadonlySet<string>;
  snapshotCollection: SnapshotCollection;
  isHighlighted?: boolean;
  // Receives the row's outcome id so the parent can pass one stable callback and keep React.memo effective.
  onHighlight?: (outcomeId: string) => void;
  hasAddNoteColumn?: boolean;
  addNoteColWidth?: number;
  columnWidths?: SnapshotGridColumnWidths;
  interactionDisabled?: boolean;
  readOnly?: boolean;
  allowReadOnlyResultDetails?: boolean;
}

export const SnapshotGridTableScrollableRow: React.FC<Props> = React.memo(
  ({
    businessReportOutcomeResultWithEvent,
    businessReportOutcome,
    promptHeaders,
    noteHeaders,
    dataColumns,
    snapshotCollection,
    isHighlighted = false,
    onHighlight,
    hasAddNoteColumn = false,
    addNoteColWidth = SNAPSHOT_GRID_ADD_NOTE_COLUMN_WIDTH,
    columnWidths,
    interactionDisabled = false,
    readOnly = false,
    allowReadOnlyResultDetails = false,
  }) => {
    const handleHighlight = onHighlight ? () => onHighlight(businessReportOutcome.id) : undefined;
    return (
      <>
        {(dataColumns ?? promptHeaders.map(name => ({ key: name, name, is_note: false }))).map(field =>
          field.is_note ? (
            <SnapshotGridTableRowNotes
              key={field.key}
              businessReportOutcome={businessReportOutcome}
              columnName={field.name}
              onRowClick={handleHighlight}
              isHighlighted={isHighlighted}
              columnWidth={getSnapshotGridColumnWidth(field.key, columnWidths)}
              interactionDisabled={interactionDisabled || readOnly}
            />
          ) : (
            <SnapshotGridTableRowResult
              businessReportOutcome={businessReportOutcome}
              snapshotCollection={snapshotCollection}
              header={field.name}
              key={field.key}
              businessReportOutcomeResultWithEvent={businessReportOutcomeResultWithEvent}
              onRowClick={handleHighlight}
              isHighlighted={isHighlighted}
              columnWidth={getSnapshotGridColumnWidth(field.key, columnWidths)}
              interactionDisabled={interactionDisabled || (readOnly && !allowReadOnlyResultDetails)}
              canEditResult={!readOnly}
            />
          ),
        )}
        {!dataColumns &&
          noteHeaders.map(noteHeader => (
            <SnapshotGridTableRowNotes
              key={`note-${noteHeader}`}
              businessReportOutcome={businessReportOutcome}
              columnName={noteHeader}
              onRowClick={handleHighlight}
              isHighlighted={isHighlighted}
              columnWidth={getSnapshotGridColumnWidth(`note:${noteHeader}`, columnWidths)}
              interactionDisabled={interactionDisabled || readOnly}
            />
          ))}
        {hasAddNoteColumn && (
          <td
            className={clsx(
              "overflow-hidden transition-colors group-hover:bg-ivory-mist-400",
              isHighlighted && "bg-blue-100",
            )}
            style={getSnapshotGridColumnStyle(addNoteColWidth)}
            onClick={handleHighlight}
          />
        )}
      </>
    );
  },
);
SnapshotGridTableScrollableRow.displayName = "SnapshotGridTableScrollableRow";

interface PipelineProps {
  row: SnapshotGridRowModel;
  fields: SnapshotGridField[];
  onOpenCell?: (row: SnapshotGridRowModel, cell: SnapshotGridRowModel["cells"][number] | null) => void;
}

export const SnapshotGridTablePipelineScrollableRow: React.FC<PipelineProps> = ({ row, fields, onOpenCell }) => (
  <>
    {fields.map(field => {
      const cell =
        row.cells.find(
          candidate => candidate.fieldKey === field.key || candidate.fieldId === field.business_report_prompt_id,
        ) ?? null;
      return (
        <SnapshotGridTablePipelineRowResult
          key={field.key}
          row={row}
          cell={cell}
          fieldName={field.name}
          onOpenCell={onOpenCell}
        />
      );
    })}
  </>
);
